import { isAxiosError } from "@ihr-radioedit/sdk-network";
import { useCallback, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { PWSSessionBackend, PwsViewUserResponse, COAST_MESSAGES } from "@inferno/renderer-shared-core";
import { Heading } from "../../../../../../core/ui";
import { FORM_ERRORS, SimpleInput, validatePasswordHintContains, validatePasswordsMatch } from "../../../forms";
import { useCoastAlerts } from "../../common/coast-alert/CoastAlerts.hook";
import { CoastAlertsDisplay } from "../../common/coast-alert/CoastAlertsDisplay.components";
import { UsersButton } from "../../common/UsersButton.components";

interface FormFields {
  confirmNewPassword: string;
  passwordHint?: string;
  newPassword: string;
  password: string;
}

interface CoastUserUpdatePasswordProps {
  userData: PwsViewUserResponse;
  session: PWSSessionBackend;
}

export const CoastUserUpdatePassword = ({ userData, session }: CoastUserUpdatePasswordProps) => {
  const { alerts, setSuccessAlert, setErrorAlert, clearAllAlerts } = useCoastAlerts();
  const { handleSubmit, register, errors, watch } = useForm<FormFields>();
  const [isProcessing, setIsProcessing] = useState(false);

  const onPasswordSubmit: SubmitHandler<FormFields> = useCallback(
    async ({ passwordHint, newPassword, password }, event) => {
      event?.preventDefault();
      setIsProcessing(true);
      clearAllAlerts();

      try {
        await session.memberService.updatePassword(userData.user.email, password, newPassword, passwordHint);
        setSuccessAlert();
      } catch (e) {
        setErrorAlert((isAxiosError(e) && e.response?.data.reason) || COAST_MESSAGES.UNEXPECTED_ERROR);
      } finally {
        setIsProcessing(false);
      }
    },
    [clearAllAlerts, session.memberService, setErrorAlert, setSuccessAlert, userData.user.email],
  );

  return (
    <section className="component-coast-user-update-password account-sub-section">
      <CoastAlertsDisplay alerts={alerts} />

      <Heading level={2}>Change Password</Heading>

      <p>To change your password, enter your current password, then your new password.</p>

      <form onSubmit={handleSubmit(onPasswordSubmit)}>
        <input type="email" hidden={true} value={userData.user.email} name="email" autoComplete="username" />

        <SimpleInput
          placeholder="Enter your password"
          rules={{ required: FORM_ERRORS.required }}
          autoComplete="current-password"
          error={errors.password}
          register={register}
          label="Current Password"
          name="password"
          type="password"
        />

        <SimpleInput
          placeholder="Enter your new password"
          rules={{
            maxLength: { value: 32, message: "Password can't be longer than 32 characters" },
            minLength: { value: 6, message: "Password can't be shorter that 6 characters" },
            required: FORM_ERRORS.required,
          }}
          autoComplete="new-password"
          error={errors.newPassword}
          register={register}
          label="New Password"
          name="newPassword"
          type="password"
        />

        <SimpleInput
          placeholder="Confirm your new password"
          rules={{
            validate: v => validatePasswordsMatch(watch("newPassword"), v),
            required: FORM_ERRORS.required,
          }}
          error={errors.confirmNewPassword}
          register={register}
          autoComplete="new-password"
          label="Confirm new Password"
          name="confirmNewPassword"
          type="password"
        />

        <SimpleInput
          placeholder="Enter your password hint"
          rules={{ validate: v => validatePasswordHintContains(v, watch("newPassword")) }}
          error={errors.passwordHint}
          register={register}
          label="Password Hint"
          name="passwordHint"
          optional={true}
          type="text"
        />

        <UsersButton
          disabled={!!Object.values(errors).length || isProcessing}
          isProcessing={isProcessing}
          label="Update password"
          type="submit"
        >
          Update
        </UsersButton>
      </form>
    </section>
  );
};
